import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { 
  ShieldCheck, 
  Check, 
  X,
  Lock,
  Shield
} from "lucide-react";

type Role = {
  id: number;
  name: string;
  description: string;
  is_system_role: boolean;
  permissions: Permission[];
};

type Permission = {
  id: number;
  name: string;
  resource: string;
  action: string;
  description: string;
};

type PermissionMatrixProps = {
  roles: Role[];
  permissions: Record<string, Permission[]>;
};

export default function PermissionMatrix({ roles, permissions }: PermissionMatrixProps) {
  const [selectedResource, setSelectedResource] = useState<string>(Object.keys(permissions)[0] || "");

  const hasPermission = (role: Role, permission: Permission): boolean => {
    return role.permissions.some(p => p.id === permission.id);
  };

  const getResourcePermissions = (resource: string) => {
    return permissions[resource] || [];
  };

  const getPermissionIcon = (hasPermission: boolean) => {
    return hasPermission ? (
      <Check className="h-4 w-4 text-green-500" />
    ) : (
      <X className="h-4 w-4 text-red-500" />
    );
  };

  return (
    <div className="space-y-6">
      {/* Resource Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            Matriz de Permissões
          </CardTitle>
          <CardDescription>
            Visualize as permissões de cada role por recurso do sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {Object.keys(permissions).map(resource => (
              <Badge
                key={resource}
                variant={selectedResource === resource ? "default" : "outline"}
                className="cursor-pointer capitalize"
                onClick={() => setSelectedResource(resource)}
              >
                {resource}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Permission Matrix */}
      {selectedResource && (
        <Card>
          <CardHeader>
            <CardTitle className="capitalize">Permissões para {selectedResource}</CardTitle>
            <CardDescription>
              Matriz mostrando quais roles têm acesso a cada ação do recurso {selectedResource}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">Role</TableHead>
                    {getResourcePermissions(selectedResource).map(permission => (
                      <TableHead key={permission.id} className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className="font-medium">{permission.action}</span>
                          <span className="text-xs text-muted-foreground">
                            {permission.description}
                          </span>
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {roles.map(role => (
                    <TableRow key={role.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {role.is_system_role ? (
                            <Lock className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Shield className="h-4 w-4 text-muted-foreground" />
                          )}
                          <div>
                            <div className="font-medium">{role.name}</div>
                            <div className="text-sm text-muted-foreground">
                              {role.description}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      {getResourcePermissions(selectedResource).map(permission => (
                        <TableCell key={permission.id} className="text-center">
                          {getPermissionIcon(hasPermission(role, permission))}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Permission Details */}
      <Card>
        <CardHeader>
          <CardTitle>Detalhes das Permissões</CardTitle>
          <CardDescription>
            Descrição detalhada de cada permissão disponível no sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(permissions).map(([resource, resourcePermissions]) => (
              <div key={resource} className="space-y-2">
                <h4 className="font-medium capitalize text-lg">{resource}</h4>
                <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                  {resourcePermissions.map(permission => (
                    <Card key={permission.id} className="p-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {permission.action}
                          </Badge>
                          <span className="text-sm font-medium">
                            {permission.name}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {permission.description}
                        </p>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Role Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Resumo dos Roles</CardTitle>
          <CardDescription>
            Visão geral das permissões de cada role
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {roles.map(role => (
              <Card key={role.id} className="p-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {role.is_system_role ? (
                        <Badge variant="default" className="flex items-center gap-1">
                          <Lock className="h-3 w-3" />
                          Sistema
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Customizado</Badge>
                      )}
                      <h4 className="font-medium">{role.name}</h4>
                    </div>
                    <Badge variant="outline">
                      {role.permissions.length} permissões
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {role.description}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {role.permissions.map(permission => (
                      <Badge key={permission.id} variant="outline" className="text-xs">
                        {permission.action}_{permission.resource}
                      </Badge>
                    ))}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}